// wiring.ts — 데이터·API 배선(개발자 소유). 컨덕터는 이 파일이 없을 때 한 번 만들고 다시 건드리지 않는다.
//  화면 파일(page.generated.tsx)은 컨덕터가 재생성 때 통째로 덮어쓴다 — 배선은 여기에만 둔다.
//  GL10 입고작업조회 1.0 → 2.0 GL18 (LOGISPRD-16038). 조회 전용. moms-be `/api/v1/global/inbound-works/search` 와
//  공통 파일 API(`/api/v1/common/files/search`·`/api/v1/common/file/urls`)만 부른다.
//  1.0 의 실적전송·DUMMY·저장은 prod 메타(BUTTONS=CLEAR,SEARCH)상 화면에 나오지 않아 옮기지 않았다(docs/plan/LOGISPRD-16038-16058-gl10.md 3장).
//  권한: 생성본의 권한 슬롯(scripts/insert-permission-slot.mjs)이 엑셀 버튼을 숨긴다. 슬롯 값은 표 슬롯이 usePermissionSlot('gl18')로 채운다.
import { useEffect, useMemo, useSyncExternalStore } from 'react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import type { CellTarget, Wiring } from './page.generated'
import { invalidError, failureError, notice } from '@/components/pbo/notice'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
import { callToAddExportLog } from '@/services/excel/apis'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { useRowMediaFiles } from '@/services/commonFile/useRowMediaFiles'
import type { CommonFileItemRes } from '@/services/commonFile/types'
import { inboundWorkSearchQueryKeys, useGetInboundWorkSearch } from '@/services/global/inboundWorkSearch/useInboundWorkSearchQueries'
import type { InboundWorkRowDto, InboundWorkSearchReq } from '@/services/global/inboundWorkSearch/types'
import {
  DEFAULT_PERIOD_DAYS_OTHER_SHIPPER,
  DEFAULT_TODAY_SHIPPER,
  INBOUND_WORK_SEARCH_MENU_CODE,
  INBOUND_WORK_SEARCH_SHIPPERS,
  MAX_EXPORT_ROWS,
  MAX_INVOICE_NOS,
  MAX_PERIOD_DAYS,
  REASON_DETAIL_MIN_LENGTH,
  WEEKDAYS,
} from '@/services/global/inboundWorkSearch/consts'
import { shipperScopeOf } from '@/components/system/shipperScope'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { queryClient } from '@/utils/queryClient'
import { ExcelActions } from '@/utils/Excel'

dayjs.extend(customParseFormat)

type Row = Record<string, unknown>

const text = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '')
const list = (v: unknown): string[] =>
  (Array.isArray(v) ? v : [v])
    .map(text)
    .map((s) => s.trim())
    .filter(Boolean)
const yyyymmdd = (v: string) => v.replaceAll('-', '')
/** 권한 슬롯 메뉴코드 — 메뉴 API 는 라우트 경로와 같은 소문자로 준다(useMenuPermission). */
const PERMISSION_MENU_CODE = INBOUND_WORK_SEARCH_MENU_CODE.toLowerCase()

/**
 * 1.0 DATE 컬럼 표시(RenderUtil.onFormatDateByDate) — `20261102100000` → `2026-11-02 (월) 10:00:00`.
 * BE 는 DB 문자열(yyyyMMddHHmmss)을 그대로 준다. 형식이 다르면 원문을 그대로 둔다.
 */
export const formatDateTime = (raw: string | null): string => {
  if (!raw) return ''
  const d = dayjs(raw, 'YYYYMMDDHHmmss', true)
  return d.isValid() ? `${d.format('YYYY-MM-DD')} (${WEEKDAYS[d.day()]}) ${d.format('HH:mm:ss')}` : raw
}

export const toViewRow = (dto: InboundWorkRowDto, media?: { atchFileCount: number; videoCount: number }): Row => ({
  rowKey: String(dto.id),
  // 생성본 셀 열기(onMediaOpen)가 record.key 로 대상 행을 잡는다 — 지금은 cellOpens 가 가로채지만 생성본 모달을 쓰게 되어도 맞게 둔다.
  key: String(dto.id),
  id: dto.id,
  shipperCode: dto.shipperCode,
  fileId: dto.fileId,
  clsDate: dto.clsDate ?? '',
  invoiceNo: dto.invoiceNo ?? '',
  ordRemark: dto.ordRemark ?? '',
  insDate: formatDateTime(dto.insDate),
  insBy: dto.insBy ?? '',
  updDate: formatDateTime(dto.updDate),
  VIDEO: media?.videoCount ?? 0,
  ATCH_FILE: media?.atchFileCount ?? 0,
})

/** 조회영역 값 → 조회 요청. 화주·기간이 비면 null(조회하지 않음). */
export const toSearchReq = (filters: Record<string, unknown>): InboundWorkSearchReq | null => {
  const shipperCode = text(filters.SPR_NM)
  const range = Array.isArray(filters.CLS_DATE) ? filters.CLS_DATE.map(text) : []
  if (!shipperCode || !range[0] || !range[1]) return null
  // 정렬해 둔다 — 같은 번호를 다른 순서로 넣어도 같은 조건(같은 queryKey)으로 본다. 서버 정렬은 입력 순서와 무관하다.
  const invoiceNos = [...new Set(list(filters.INVOICE_NO))].sort((a, b) => a.localeCompare(b))
  return {
    shipperCode,
    clsDateFrom: yyyymmdd(range[0]),
    clsDateTo: yyyymmdd(range[1]),
    ...(invoiceNos.length ? { invoiceNos } : {}),
  }
}

// ── 화주 범위 ────────────────────────────────────────────────────────────────
// 1.0 메타는 MUSINSA·MUSINSAGLOBAL·NIKE·NIKESAMPLE 만 있다. 화주 계정은 자기 화주로 잠근다(2.0 공용 규칙 shipperScopeOf).
// 서버도 허용 밖 화주는 빈 목록으로 돌려준다 — 화면 잠금은 편의이고 판정은 서버가 한다.
const scope = () => shipperScopeOf(AuthStorage.user.get())
const shipperOptions = () => {
  const s = scope()
  if (s.locked) return [{ value: s.shipperCode, label: s.shipperName || s.shipperCode }]
  return INBOUND_WORK_SEARCH_SHIPPERS.map((code) => ({ value: code, label: code }))
}
const SHIPPER_SET = new Set<string>(INBOUND_WORK_SEARCH_SHIPPERS)
const defaultShipper = (): string => {
  const s = scope()
  if (s.locked) return s.shipperCode
  return SHIPPER_SET.has(s.shipperCode) ? s.shipperCode : INBOUND_WORK_SEARCH_SHIPPERS[0]
}
/** 1.0 systemTask.py:136 — MUSINSA 는 당일, 그 밖의 화주는 7일 전부터 오늘까지 */
export const defaultClsDate = (shipperCode: string, today = dayjs()): [string, string] => [
  (shipperCode === DEFAULT_TODAY_SHIPPER ? today : today.subtract(DEFAULT_PERIOD_DAYS_OTHER_SHIPPER, 'day')).format('YYYY-MM-DD'),
  today.format('YYYY-MM-DD'),
]

/** 조회 요청 검증 — 막을 때 안내 문구, 통과하면 null. [조회] 와 URL 복원이 같은 기준을 쓴다. */
const invalidReasonOf = (req: InboundWorkSearchReq): string | null => {
  // strict 파싱 — 형식이 어긋나면 diff 가 NaN 이 되어 상한 검사를 조용히 통과하므로, 그 경우도 막는다.
  const from = dayjs(req.clsDateFrom, 'YYYYMMDD', true)
  const to = dayjs(req.clsDateTo, 'YYYYMMDD', true)
  if (!from.isValid() || !to.isValid()) return '마감일자를 다시 선택해 주세요.'
  // 날짜 선택기는 뒤집힌 기간을 만들지 않는다. URL 을 손으로 고친 복원 경로만 여기 걸린다.
  if (from.isAfter(to)) return '마감일자 시작일이 종료일보다 늦습니다.'
  if (to.diff(from, 'day') >= MAX_PERIOD_DAYS) return `마감일자는 최대 ${MAX_PERIOD_DAYS}일까지 조회할 수 있습니다.`
  if ((req.invoiceNos?.length ?? 0) > MAX_INVOICE_NOS) return `입고송장번호는 최대 ${MAX_INVOICE_NOS}건까지 조회할 수 있습니다.`
  return null
}

// ── URL·탭 세션 복원 ────────────────────────────────────────────────────────────
// 다른 메뉴에 다녀오면 화면이 다시 마운트된다. [조회] 로 확정한 조건을 URL 과 탭 세션(menuOnSession)에 싣고,
// 진입 때 URL 에서 되살려 같은 조회를 다시 건다. 탭을 닫으면 탭 세션이 지워져 다음 진입은 빈 화면이다(GI17 LOGISPRD-17344 선례).
// 입고송장번호 목록은 1000건까지라 URL 에 싣지 않는다 — 아래 saveSearchLists 가 sessionStorage 에 따로 둔다(GI17 LOGISPRD-17358).

/** URL·탭 세션에서 되살릴 수 있는 화주 — 1.0 메타 화주이고, 화주 계정이면 자기 화주만. */
const canRestoreShipper = (shipperCode: string): boolean => {
  const s = scope()
  return s.locked ? s.shipperCode === shipperCode : SHIPPER_SET.has(shipperCode)
}

/** URL 쿼리 → 조회 요청. 화주·기간이 없거나, 고를 수 없는 화주이거나, [조회] 가 막는 조건이면 null(복원하지 않음). */
export const fromSearchQuery = (query: Record<string, unknown>): InboundWorkSearchReq | null => {
  const shipperCode = text(query.shipperCode)
  const clsDateFrom = text(query.clsDateFrom)
  const clsDateTo = text(query.clsDateTo)
  if (!shipperCode || !clsDateFrom || !clsDateTo || !canRestoreShipper(shipperCode)) return null
  // [조회] 와 같은 정규화(공백·중복 제거, 정렬) — 같은 조건 판정(search 의 same)이 문자열 비교라 모양이 같아야 한다.
  const invoiceNos = [...new Set(list(query.invoiceNos ?? []))].sort((a, b) => a.localeCompare(b))
  const req: InboundWorkSearchReq = { shipperCode, clsDateFrom, clsDateTo, ...(invoiceNos.length ? { invoiceNos } : {}) }
  return invalidReasonOf(req) ? null : req
}

/** 조회 요청 → 조회영역 값. toSearchReq 의 역이다. */
export const toFilters = (req: InboundWorkSearchReq): Record<string, unknown> => ({
  SPR_NM: req.shipperCode,
  CLS_DATE: [dayjs(req.clsDateFrom, 'YYYYMMDD').format('YYYY-MM-DD'), dayjs(req.clsDateTo, 'YYYYMMDD').format('YYYY-MM-DD')],
  INVOICE_NO: req.invoiceNos?.length ? [...req.invoiceNos] : undefined,
})

export const SEARCH_LISTS_KEY = 'moms_gl18_search_lists'
type SavedLists = { key: string; invoiceNos: string[] }

/** URL·탭 세션에 싣는 조건 — 조회 요청에서 입고송장번호 목록만 뺀다. */
export type InboundWorkUrlQuery = Omit<InboundWorkSearchReq, 'invoiceNos'>
export const toUrlQuery = (req: InboundWorkSearchReq): InboundWorkUrlQuery => {
  const { invoiceNos: _invoiceNos, ...rest } = req
  return rest
}

/** [조회] 로 확정한 목록을 URL 조건과 짝지어 둔다. 목록이 없거나 null 이면 지운다. 저장소를 못 쓰면 목록 없이 복원된다. */
export const saveSearchLists = (req: InboundWorkSearchReq | null) => {
  try {
    if (!req?.invoiceNos?.length) {
      sessionStorage.removeItem(SEARCH_LISTS_KEY)
      return
    }
    const saved: SavedLists = { key: JSON.stringify(toUrlQuery(req)), invoiceNos: req.invoiceNos }
    sessionStorage.setItem(SEARCH_LISTS_KEY, JSON.stringify(saved))
  } catch {
    // 저장소가 막힌 브라우저(사생활 보호 모드 등) — 목록 없이 복원한다
  }
}

/** URL 에서 되살린 조건에 같은 조건으로 저장해 둔 목록을 붙인다. 조건이 다르면(다른 조회의 목록) 붙이지 않는다. */
export const withSavedLists = (fromUrl: InboundWorkSearchReq | null): InboundWorkSearchReq | null => {
  if (!fromUrl) return null
  try {
    const saved = JSON.parse(sessionStorage.getItem(SEARCH_LISTS_KEY) ?? 'null') as SavedLists | null
    if (!saved || saved.key !== JSON.stringify(toUrlQuery(fromUrl))) return fromUrl
    // fromSearchQuery 로 다시 만들어 정규화·검증(1000건 상한)을 [조회] 와 맞춘다
    return fromSearchQuery({ ...fromUrl, invoiceNos: saved.invoiceNos ?? [] }) ?? fromUrl
  } catch {
    return fromUrl
  }
}

/** 조건을 URL·탭 세션에 싣는 함수 — 라우터 훅이 필요해 엔트리(index.tsx)가 묶는다. null 이면 비운다. */
type Persist = (req: InboundWorkSearchReq | null) => void
let persist: Persist | null = null
export const bindInboundWorkSearchPersist = (fn: Persist | null) => {
  persist = fn
}

// ── 모듈 상태 ────────────────────────────────────────────────────────────────
// params = [조회] 로 확정한 조건. 표 슬롯은 입력 중인 조회영역 값을 받으므로 따로 들고 있어야 입력만으로 재조회되지 않는다.
// media = 첨부·동영상 모달 대상과 행별 파일 목록. 모달은 생성본의 DS 모달(이름만) 대신 index.tsx 의 공용 MediaModal(열람 가능)이 그린다.
export interface MediaState {
  target: { kind: 'video' | 'attach'; row: { id: number; fileId: string | null }; shipper: string } | null
  filesByFileId: Map<string, CommonFileItemRes>
}
let params: InboundWorkSearchReq | null = null
/**
 * 확정 조건의 조회 결과 — 표 슬롯이 커밋 뒤 채운다. 엑셀 버튼 가드가 읽는다.
 * fetching = 응답 대기 중, count = 서버가 준 행 수(조회 전·실패면 null).
 * 생성본은 목록 검색어를 ActionCtx 로 넘기지 않으므로, 보이는 행 0건이 목록 검색 때문인지는 이 원본 행 수로 가른다.
 */
let loaded: { fetching: boolean; count: number | null } = { fetching: false, count: null }
let media: MediaState = { target: null, filesByFileId: new Map() }
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())
const setParams = (next: InboundWorkSearchReq | null) => {
  params = next
  emit()
}
const setMedia = (next: Partial<MediaState>) => {
  media = { ...media, ...next }
  emit()
}
const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}
/** index.tsx 의 MediaModal 이 읽는다. */
export const useMediaState = (): MediaState => useSyncExternalStore(subscribe, () => media)
export const closeMedia = () => setMedia({ target: null })

/** 진입 때 복원한 조건 — 조회영역(filterInitial)에도 같은 값을 그린다. */
let restored: InboundWorkSearchReq | null = null

/**
 * 마운트 때 첫 렌더 전에 부른다. URL 에서 되살린 조건이 있으면 그 조건으로 조회하고, 없으면 빈 화면에서 시작한다(배선 체크 ⑤).
 * 모달 대상은 늘 비운다 — 다시 들어왔을 때 모달이 열린 채 뜨지 않게.
 */
export const initInboundWorkSearchStore = (fromUrl: InboundWorkSearchReq | null) => {
  restored = fromUrl
  params = fromUrl
  loaded = { fetching: false, count: null }
  media = { target: null, filesByFileId: new Map() }
  bridge.slot = null
  emit()
}
export const resetInboundWorkSearchStore = () => initInboundWorkSearchStore(null)

/** 표 슬롯이 마운트 동안 채우는 다리. 생성본이 같은 렌더에서 wiring.permission 을 읽는다. */
const bridge = {
  slot: null as ReturnType<typeof usePermissionSlot> | null,
}

const refetch = () => queryClient.invalidateQueries({ queryKey: inboundWorkSearchQueryKeys.all })

/** 엑셀 컬럼 — 표 exportColumns 와 같은 순서. 1.0 COLUMN_EXP=1 컬럼(동영상·첨부는 0). */
export const EXPORT_COLUMNS = [
  { field: 'clsDate', headerName: '마감일자' },
  { field: 'invoiceNo', headerName: '송장번호' },
  { field: 'ordRemark', headerName: '상태값' },
  { field: 'insDate', headerName: '생성일자' },
  { field: 'insBy', headerName: '작업자' },
  { field: 'updDate', headerName: '수정일자' },
]

/**
 * 엑셀 버튼·다운로드 확인에서 내려받을 원본이 없을 때의 안내. 원본이 있으면 null(보이는 행 0건은 목록 검색 때문이다).
 * 조회 0건·응답 전·조회 실패(예: moms-be 10만 건 초과 400)는 목록 검색 안내로 보내지 않는다.
 */
const emptyExportMessage = (): string | null => {
  if (params === null) return '다운로드할 데이터가 없습니다. 조회 후 다운로드해 주세요.'
  if (loaded.fetching) return '조회가 끝난 뒤 다운로드해 주세요.'
  if (!loaded.count) return '다운로드할 데이터가 없습니다. 조회 결과를 확인해 주세요.'
  return null
}

/**
 * 첨부·동영상 셀 → 공용 MediaModal. false 를 돌려 생성본의 DS 모달은 열지 않는다(RGR02 선례).
 * 파일이 없는 행(fileId 없음)도 연다 — 1.0 GL10 도 빈 행에서 팝업이 열렸다. 모달은 "등록된 파일이 없습니다." 를 보인다.
 */
const openMedia = (kind: 'video' | 'attach', target: CellTarget): boolean => {
  const row = target.row
  const id = typeof row.id === 'number' ? row.id : Number(row.id)
  const fileId = text(row.fileId) || null
  if (Number.isFinite(id)) setMedia({ target: { kind, row: { id, fileId }, shipper: text(row.shipperCode) } })
  return false
}

export const wiring: Wiring = {
  rows: {
    ROWS: () => {
      const slot = usePermissionSlot(PERMISSION_MENU_CODE)
      // 렌더 중 대입은 의도다 — 생성본은 이 훅을 부른 바로 뒤 같은 렌더에서 wiring.permission 을 읽는다(MA19 선례).
      bridge.slot = slot
      const p = useSyncExternalStore(subscribe, () => params)
      const { data, isFetching } = useGetInboundWorkSearch(p)
      const fileIds = useMemo(() => (data ?? []).map((d) => d.fileId ?? '').filter(Boolean), [data])
      const { filesByFileId, counts } = useRowMediaFiles(INBOUND_WORK_SEARCH_MENU_CODE, p?.shipperCode ?? '', fileIds)
      // 커밋된 파일 목록만 모듈 상태에 싣고 알린다(렌더 중에 바꾸지 않는다). 목록이 오기 전에 모달을 열어도 도착하면 모달이 받는다.
      useEffect(() => {
        if (media.filesByFileId !== filesByFileId) setMedia({ filesByFileId })
      }, [filesByFileId])
      const rows = useMemo(() => (data ?? []).map((d) => toViewRow(d, d.fileId ? counts.get(d.fileId) : undefined)), [data, counts])
      useEffect(() => {
        loaded = { fetching: isFetching, count: p && data ? data.length : null }
      }, [p, data, isFetching])
      // total 을 넘기지 않는다 — 생성본은 total 이 있으면 서버 페이징으로 보고 배열을 자르지 않는다. 전건을 받으므로 클라이언트 페이징이다.
      return { rows, loading: isFetching }
    },
  },
  actions: {
    search: (filters) => {
      if (!text(filters.SPR_NM)) {
        notice.invalid('화주명을 선택해 주세요.')
        return false
      }
      const next = toSearchReq(filters)
      if (!next) {
        notice.invalid('마감일자를 입력해 주세요.')
        return false
      }
      const reason = invalidReasonOf(next)
      if (reason) {
        notice.invalid(reason)
        return false
      }
      // toSearchReq·fromSearchQuery 가 늘 같은 순서로 키를 만들어서 문자열 비교로 같은 조건인지 가린다.
      const same = JSON.stringify(next) === JSON.stringify(params)
      setParams(next)
      // 같은 조건 재조회는 queryKey 가 같아 요청이 나가지 않는다 — 무효화로 다시 받는다(배선 체크 ⑥).
      if (same) refetch().catch(() => undefined)
      persist?.(next)
      return undefined
    },
    gl18DownloadModal_ok: async (ctx) => {
      const reason = text(ctx.form?.reason)
      const detail = text(ctx.form?.detail).trim()
      if (!reason) throw invalidError('사유코드를 선택해 주세요.')
      if (!detail) throw invalidError('상세사유를 입력해 주세요.')
      if (detail.length < REASON_DETAIL_MIN_LENGTH) throw invalidError(`상세사유는 ${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해 주세요.`)
      const rows = ctx.visible ?? []
      if (!rows.length) throw invalidError(emptyExportMessage() ?? '다운로드할 데이터가 없습니다. 목록 검색어를 확인해 주세요.')
      // 1.0 ToolBar.js 와 같은 상한. 기록을 남기기 전에 막는다 — 내려주지 않을 반출을 기록하지 않게.
      if (rows.length > MAX_EXPORT_ROWS)
        throw invalidError(`엑셀은 최대 ${MAX_EXPORT_ROWS.toLocaleString()}건까지 내려받을 수 있습니다. 조회 기간을 줄여 주세요.`)
      // 감사로그를 먼저 남긴다 — 기록이 실패하면 파일을 내려주지 않는다(사유 없는 반출 방지, CR-01).
      await callToAddExportLog({
        menuCode: INBOUND_WORK_SEARCH_MENU_CODE,
        apiName: `${INBOUND_WORK_SEARCH_MENU_CODE}_LIST_EXPORT`,
        reason: `${reason}_${detail}`,
      }).catch(() => {
        throw failureError('다운로드 기록을 남기지 못해 다운로드하지 않았습니다. 잠시 뒤 다시 시도해 주세요.')
      })
      await new ExcelActions().download({ columnDefs: EXPORT_COLUMNS, rowData: rows, fileName: '입고작업조회' })
      return '다운로드했습니다.'
    },
    // 첨부·동영상은 cellOpens 가 공용 MediaModal 로 돌린다 — 생성본의 DS 모달은 열리지 않아 이 두 슬롯에 닿지 않는다(닿으면 모달 유지).
    gl18VideoModal_ok: () => false,
    gl18AttachModal_ok: () => false,
  },
  // 사유를 다 쓰고 나서야 거절되지 않게, 원본이 없으면 모달을 열기 전에 막는다.
  opens: {
    gl18ExcelButton: () => {
      const message = emptyExportMessage()
      if (!message) return true
      notice.invalid(message)
      return false
    },
  },
  cellOpens: {
    gl18VideoModal: (target) => openMedia('video', target),
    gl18AttachModal: (target) => openMedia('attach', target),
  },
  // 버튼 권한 슬롯 — 생성본이 엑셀 버튼을 숨긴다(permTool). 마운트 전에는 비워 둬 전부 보인다(생성본 기본).
  get permission() {
    return bridge.slot ?? undefined
  },
  form: {
    gl18DownloadModal: (state) => state,
  },
  options: {
    GL18SHIPPER_OPTIONS: () => shipperOptions(),
    // 다운로드 사유코드 — 콤보 DOWNLOAD_CD(2.0 공통)
    // 배선 계약: options 슬롯은 생성본 컴포넌트 본문에서 매 렌더 같은 순서로 불린다 — 안에서 조회 훅을 써도 된다(wiring.skeleton.ts).
    REASONRADIO_OPTIONS: (demo) => {
      const { data } = useGetExportCombo({ errorToast: true })
      const cds = data?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []
      const nms = data?.DOWNLOAD_CD?.COMBO_OPT_NM ?? []
      const options = cds.map((value, i) => ({ value, label: nms[i] ?? value })).filter((o) => o.value)
      return options.length ? options : demo
    },
  },
  // 게터 — 모듈을 읽는 시점(로그인 전일 수 있다)이 아니라 화면이 그릴 때 로그인 사용자로 판정한다.
  filterDisabled: {
    get SPR_NM() {
      return scope().locked
    },
  },
  filterDefaults: {
    gl18FilterCard: () => {
      const shipperCode = defaultShipper()
      return { SPR_NM: shipperCode, CLS_DATE: defaultClsDate(shipperCode) }
    },
  },
  // 진입 때 URL 에서 되살린 조건을 조회영역에도 그린다. [초기화] 는 이 값이 아니라 filterDefaults 로 돌아간다.
  filterInitial: {
    gl18FilterCard: (defaults) => (restored ? { ...defaults, ...toFilters(restored) } : defaults),
  },
  // [초기화] 는 조회영역만 기본값으로 돌린다. 실어 둔 조건도 지워 다음 진입이 초기화한 상태에서 시작하게 한다(GI17 선례).
  onReset: () => persist?.(null),
}
